/**
 * Real-Time Mode store — the live patient-match state for the sidebar.
 *
 * Replaces the placeholder `skill-store.ts`. Models the full PRD §6 match
 * state machine via the typed discriminated union from
 * `@ctrl-enter/types/integrations`.
 *
 * Architectural rules (per workspace conventions):
 *   - State management only; no API calls, no business logic.
 *   - React hooks orchestrate API + multi-store calls.
 *   - Atomic selectors only — never destructure the store hook.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EnterPatient, MatchState, ObservedContext } from '@ctrl-enter/types';

type RealTimeModeStore = {
  /** Current match state — the single source of truth for the sidebar. */
  match: MatchState;

  /** Replace the match state wholesale (typical update path). */
  setMatch: (next: MatchState) => void;

  /** Mark loading for a freshly observed context. */
  setLoading: (context: ObservedContext) => void;

  /** Reset to idle (sidebar dismissed / page navigated away). */
  reset: () => void;

  /**
   * User picked a candidate from the `multiple` state. The component
   * holds the picked patient until the hook orchestrator re-resolves with
   * it as a confirmed-id hint.
   */
  pickCandidate: (patient: EnterPatient) => void;
  /** Currently-picked candidate from `multiple` (transient UI state). */
  pickedCandidate: EnterPatient | null;

  /**
   * The Enter account ID that produced the most recent `matched`/`likely`
   * result. The chat toolbar watches this and auto-selects the matching
   * MCP account so the AI talks to the right practice. Cleared on reset.
   */
  suggestedMcpAccountId: string | null;
  setSuggestedMcpAccountId: (id: string | null) => void;
};

const initialMatch: MatchState = { status: 'idle' };

export const useRealTimeModeStore = create<RealTimeModeStore>()(
  devtools(
    set => ({
      match: initialMatch,
      pickedCandidate: null,
      suggestedMcpAccountId: null,
      setMatch: next => set({ match: next, pickedCandidate: null }),
      setLoading: context => set({ match: { status: 'loading', context } }),
      reset: () => set({ match: initialMatch, pickedCandidate: null, suggestedMcpAccountId: null }),
      pickCandidate: patient => set({ pickedCandidate: patient }),
      setSuggestedMcpAccountId: id => set({ suggestedMcpAccountId: id }),
    }),
    { name: 'real-time-mode-store' }
  )
);

// ─── Atomic selectors ─────────────────────────────────────────────────────

export const useRealTimeModeMatch = () => useRealTimeModeStore(s => s.match);
export const useRealTimeModeSetMatch = () => useRealTimeModeStore(s => s.setMatch);
export const useRealTimeModeSetLoading = () => useRealTimeModeStore(s => s.setLoading);
export const useRealTimeModeReset = () => useRealTimeModeStore(s => s.reset);
export const useRealTimeModePickedCandidate = () =>
  useRealTimeModeStore(s => s.pickedCandidate);
export const useRealTimeModePickCandidate = () =>
  useRealTimeModeStore(s => s.pickCandidate);
export const useRealTimeModeSuggestedMcpAccountId = () =>
  useRealTimeModeStore(s => s.suggestedMcpAccountId);
export const useRealTimeModeSetSuggestedMcpAccountId = () =>
  useRealTimeModeStore(s => s.setSuggestedMcpAccountId);
